import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CustomHeader = ({ activePage = '' }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu when location changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('.navbar')) {
        setMenuOpen(false);
      }
    };

    // Handle escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  // Helper to close menu on link click
  const handleLinkClick = () => setMenuOpen(false);

  // Toggle menu
  const toggleMenu = () => setMenuOpen(!menuOpen);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    }
  };

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      {/* Logo */}
      <div className="logo-container">
        <img src="/logo.PNG" alt="IntelUniVen Logo" className="logo circle-logo" />
      </div>
      
      {/* Hamburger Button */}
      <button
        className="hamburger-btn"
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        aria-label="Toggle navigation menu"
        aria-expanded={menuOpen}
        aria-controls="main-navigation"
      >
        <span className={`hamburger-bar ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
        <span className={`hamburger-bar ${menuOpen ? 'opacity-0' : ''}`}></span>
        <span className={`hamburger-bar ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
      </button>
      
      {/* Navigation Links */}
      <ul 
        id="main-navigation"
        className={`nav-links${menuOpen ? ' open' : ''}`}
        role="menu"
        aria-hidden={!menuOpen}
      >
        <li role="none">
          <a 
            href="/" 
            className={activePage === 'home' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            Home
          </a>
        </li>
        <li role="none">
          <a 
            href="/services" 
            className={activePage === 'services' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            Services
          </a>
        </li>
        <li role="none">
          <a 
            href="/products" 
            className={activePage === 'products' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            Products
          </a>
        </li>
        <li role="none">
          <a 
            href="/academy" 
            className={activePage === 'academy' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            Academy
          </a>
        </li>
        <li role="none">
          <a 
            href="/contact" 
            className={activePage === 'contact' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            Contact
          </a>
        </li>
        <li role="none">
          <a 
            href="/about" 
            className={activePage === 'about' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            About Us
          </a>
        </li>
        <li role="none">
          <a 
            href="/login" 
            className={activePage === 'login' ? 'active' : ''} 
            onClick={handleLinkClick}
            role="menuitem"
            tabIndex={menuOpen ? 0 : -1}
          >
            Login
          </a>
        </li>
      </ul>
      
      {/* Overlay for mobile menu */}
      {menuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={handleLinkClick}
          aria-hidden="true"
        ></div>
      )}
    </nav>
  );
};

export default CustomHeader;